import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Archive, ClipboardCheck, Package, RefreshCw, ShieldCheck } from 'lucide-react';
import { dashboardService, DashboardSummary } from '../../services/dashboardService';
import DismissibleError from '../DismissibleError';

type OperationalRole = 'CENTRAL_LOGISTICS_OFFICER' | 'INSPECTION_OFFICER' | 'ACCOUNTABLE_PNP_OFFICER';

const ROLE_CONTENT: Record<OperationalRole, {
  title: string;
  subtitle: string;
  section: string;
  modules: Array<[string, string]>;
}> = {
  CENTRAL_LOGISTICS_OFFICER: {
    title: 'Logistics Dashboard',
    subtitle: 'Firearm inventory, custody, and issuance operations.',
    section: 'Logistics Operations',
    modules: [
      ['Inventory registry', 'Review firearm records, serviceability, and availability'],
      ['Custody and issuance', 'Track active issuances, returns, and custody history'],
      ['Incident monitoring', 'Review lost, damaged, and under-investigation firearms'],
    ],
  },
  INSPECTION_OFFICER: {
    title: 'Inspector Dashboard',
    subtitle: 'Inspection missions, verification, and discrepancy review.',
    section: 'Inspection Operations',
    modules: [
      ['Inspection missions', 'Prepare and review Form 18 inspection activities'],
      ['Firearm verification', 'Validate physical firearms against registry records'],
      ['Evidence and findings', 'Review photographic evidence and discrepancies'],
    ],
  },
  ACCOUNTABLE_PNP_OFFICER: {
    title: 'Accountable Officer Dashboard',
    subtitle: 'Personnel accountability, ARE records, and assigned firearms.',
    section: 'Accountability Operations',
    modules: [
      ['Personnel records', 'Maintain accountable officer identity and eligibility records'],
      ['ARE monitoring', 'Review acknowledgement receipt validity and assignments'],
      ['Returns and accountability', 'Process returns and maintain custody documentation'],
    ],
  },
};

interface RoleDashboardProps {
  role: OperationalRole;
  canViewSummary: boolean;
}

const RoleDashboard: React.FC<RoleDashboardProps> = ({ role, canViewSummary }) => {
  const content = ROLE_CONTENT[role];
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(canViewSummary);
  const [error, setError] = useState('');

  const loadSummary = useCallback(async () => {
    if (!canViewSummary) return;
    try {
      setLoading(true);
      setError('');
      setSummary(await dashboardService.getSummary());
    } catch {
      setError('Operational summary is temporarily unavailable.');
    } finally {
      setLoading(false);
    }
  }, [canViewSummary]);

  useEffect(() => { void loadSummary(); }, [loadSummary]);

  if (loading) return <div className="loading-state"><span className="loading-mark" />Loading operational overview…</div>;

  const metrics = summary ? [
    ['Total Inventory', summary.total_inventory, 'Registered firearms', Package, 'blue'],
    ['Issued', summary.status_distribution.issued_active_duty, 'Active-duty custody', ShieldCheck, 'green'],
    ['In Storage', summary.status_distribution.returned_in_storage, 'Returned firearms', Archive, 'purple'],
    ['Discrepancies', summary.discrepancy_alerts.length, 'Items requiring review', AlertTriangle, 'neutral'],
  ] as const : [];

  return (
    <div className="admin-dashboard operational-dashboard">
      <div className="page-heading">
        <div><h1>{content.title}</h1><p>{content.subtitle}</p></div>
        {canViewSummary && <button className="btn btn-secondary" onClick={loadSummary}><RefreshCw size={14} />Refresh data</button>}
      </div>

      {error && <DismissibleError message={error} onDismiss={() => setError('')} onRetry={loadSummary} />}

      {metrics.length > 0 && <div className="stats-grid">
        {metrics.map(([label, value, note, Icon, tone]) => <article className="stat-card" key={label}><span className={`metric-icon ${tone}`}><Icon size={20} /></span><div><span className="stat-label">{label}</span><strong className="stat-number">{value}</strong><span className="stat-note">{note}</span></div></article>)}
      </div>}

      <div className="dashboard-grid">
        <section className="panel">
          <div className="section-heading"><div><h2>{content.section}</h2><p>Capabilities available for your assigned CyberShield role.</p></div></div>
          <div className="module-list">
            {content.modules.map(([title, copy]) => <div className="module-row" key={title}><span className="module-icon"><ClipboardCheck size={15} /></span><div><strong>{title}</strong><p>{copy}</p></div></div>)}
          </div>
        </section>

        <aside className="dashboard-side">
          <section className="panel system-health"><div className="section-heading"><h2>Access Status</h2><span>RBAC</span></div><div className="health-list"><div><span>Authentication</span><strong><ShieldCheck size={13} /> Verified</strong></div><div><span>Assigned role</span><strong><ShieldCheck size={13} /> Active</strong></div><div><span>Session protection</span><strong><ShieldCheck size={13} /> Enabled</strong></div></div><p>Your workspace is limited to authorized role capabilities.</p></section>
        </aside>
      </div>
    </div>
  );
};

export default RoleDashboard;
